import { useMemo, useState } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import {
  ArrowUpRight,
  ChevronDown,
  Workflow,
  Sparkles,
  Settings2,
} from 'lucide-react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  GithubLinkButton,
  GithubMark,
  PageHeader,
  SectionHeader,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  VercelMark,
} from '@loop3/ui'
import type { RepoEntry } from '@loop3/sync'
import type { ComponentType, SVGProps } from 'react'
import {
  DISTRIBUTION,
  REPOS,
  githubSourceUrl,
  type DistributionCategory,
  type ManagedFile,
} from '@/lib/distribution'

/** Repos a managed file is synced to, based on scope + repo-level `include`. */
function targetsFor(item: ManagedFile, repos: RepoEntry[]): RepoEntry[] {
  const active = repos.filter((r) => r.sync !== false)
  if (item.scope === 'global') return active
  return active.filter((r) => r.include?.includes(item.id))
}

type IconType = ComponentType<SVGProps<SVGSVGElement>>

const CATEGORY_META: Record<
  DistributionCategory,
  { title: string; subtitle: string; icon: IconType; githubUrl: string }
> = {
  workflows: {
    title: 'Workflows',
    subtitle: 'GitHub Actions automations synced to every managed repo.',
    icon: Workflow,
    githubUrl: 'https://github.com/Loop3Studio/l3-os/tree/main/.ops/workflows',
  },
  skills: {
    title: 'Skills',
    subtitle: 'Claude Code skills available when working in managed repos.',
    icon: Sparkles,
    githubUrl: 'https://github.com/Loop3Studio/l3-os/tree/main/.ops/skills',
  },
  configs: {
    title: 'Configs',
    subtitle: 'Shared settings, ignorelists, and git hooks.',
    icon: Settings2,
    githubUrl: 'https://github.com/Loop3Studio/l3-os/tree/main/.ops/configs',
  },
}

function ScopeBadge({ scope }: { scope: ManagedFile['scope'] }) {
  return (
    <span
      className={`rounded px-1.5 py-0.5 font-mono text-[0.6rem] uppercase tracking-[0.14em] ${
        scope === 'global'
          ? 'bg-[var(--l3-violet-soft)] text-[var(--l3-violet)]'
          : 'bg-muted text-muted-foreground'
      }`}
    >
      {scope}
    </span>
  )
}

function OriginBadge({ origin }: { origin: ManagedFile['origin'] }) {
  if (origin === null) return null
  return (
    <span
      className={`rounded px-1.5 py-0.5 font-mono text-[0.6rem] uppercase tracking-[0.14em] ${
        origin === 'l3'
          ? 'bg-[var(--l3-violet-soft)] text-[var(--l3-violet)]'
          : 'bg-muted text-muted-foreground'
      }`}
    >
      {origin === 'l3' ? 'L3' : 'COMMUNITY'}
    </span>
  )
}

function TargetsLabel({ item }: { item: ManagedFile }) {
  const targets = useMemo(() => targetsFor(item, REPOS), [item])

  if (item.scope === 'global') {
    return (
      <span className="shrink-0 self-center font-mono text-[0.65rem] uppercase tracking-[0.14em] opacity-50">
        All repos · {targets.length}
      </span>
    )
  }
  if (targets.length === 0) {
    return (
      <span className="shrink-0 self-center font-mono text-[0.65rem] uppercase tracking-[0.14em] opacity-40">
        Unused
      </span>
    )
  }
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- hover-only tooltip trigger nested in the row's outer <a>; onClick only stops accidental link navigation, there's no action for keyboard users to reach
          <span
            className="shrink-0 cursor-help self-center font-mono text-[0.65rem] uppercase tracking-[0.14em] opacity-60 hover:opacity-100"
            onClick={(e) => e.preventDefault()}
          />
        }
      >
        {targets.length} {targets.length === 1 ? 'repo' : 'repos'}
      </TooltipTrigger>
      <TooltipContent className="max-w-sm flex-col items-start gap-1 py-2 text-left">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.14em] opacity-60">
          Target repos
        </div>
        <ul className="flex flex-col gap-0.5 font-mono text-[0.7rem]">
          {targets.map((t) => (
            <li key={t.repo}>{t.repo}</li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  )
}

function DistributionRow({ item }: { item: ManagedFile }) {
  return (
    <a
      href={githubSourceUrl(item.source)}
      target="_blank"
      rel="noopener noreferrer"
      className="group block border-b border-border py-4 px-4 hover:bg-[var(--l3-violet-soft)] transition-colors"
    >
      <div className="mb-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        <h3 className="text-sm font-medium transition-colors group-hover:text-[var(--l3-violet)]">
          {item.id}
        </h3>
        <ScopeBadge scope={item.scope} />
        <OriginBadge origin={item.origin} />
        <span aria-hidden="true" className="opacity-30">
          ·
        </span>
        <TargetsLabel item={item} />
        <ArrowUpRight className="size-3.5 shrink-0 opacity-30 transition-opacity group-hover:opacity-80" />
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {item.referenceUrl ? (
          <a
            href={item.referenceUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="group/src inline-flex items-center gap-1.5 font-mono text-[0.7rem] leading-relaxed opacity-60 transition-colors hover:text-[var(--l3-violet)] hover:underline"
          >
            {item.origin === 'l3' ? (
              <GithubMark className="size-3.5" />
            ) : (
              <VercelMark className="size-3.5" />
            )}
            {item.origin === 'l3'
              ? 'View in L3OS'
              : (item.sourceRepo ?? item.source)}
            <ArrowUpRight className="size-3 opacity-50 transition-opacity group-hover/src:opacity-100" />
          </a>
        ) : (
          <p className="font-mono text-[0.7rem] leading-relaxed opacity-60">
            {item.source}
          </p>
        )}
        {item.origin === 'community' && (
          <a
            href={githubSourceUrl(item.source)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="group/l3 inline-flex items-center gap-1.5 font-mono text-[0.7rem] leading-relaxed opacity-60 transition-colors hover:text-[var(--l3-violet)] hover:underline"
          >
            <GithubMark className="size-3.5" />
            View in L3OS
            <ArrowUpRight className="size-3 opacity-50 transition-opacity group-hover/l3:opacity-100" />
          </a>
        )}
      </div>
    </a>
  )
}

function OriginGroup({
  label,
  items,
}: {
  label: string
  items: ManagedFile[]
}) {
  const [open, setOpen] = useState(true)
  if (items.length === 0) return null
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full cursor-pointer items-center gap-2.5 border-y border-border bg-muted/40 px-3 py-3 text-left transition-colors hover:bg-[var(--l3-violet-soft)]/60">
        <ChevronDown
          className={`size-4 opacity-70 transition-transform duration-200 ease-out ${open ? 'rotate-0' : '-rotate-90'}`}
        />
        <span className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.14em]">
          {label}
        </span>
        <span className="font-mono text-[0.7rem] opacity-50">
          · {items.length}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {items.map((item) => (
          <DistributionRow key={item.id} item={item} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}

const VALID_CATEGORIES: DistributionCategory[] = [
  'workflows',
  'skills',
  'configs',
]

export function DistributionCategoryPage() {
  const { category } = useParams<{ category: string }>()

  if (
    !category ||
    !VALID_CATEGORIES.includes(category as DistributionCategory)
  ) {
    return <Navigate to="/l3os-center" replace />
  }

  const cat = category as DistributionCategory
  const meta = CATEGORY_META[cat]
  const items = DISTRIBUTION[cat]
  const Icon = meta.icon

  return (
    <div className="mx-auto max-w-7xl pb-16">
      <PageHeader
        eyebrow="L3OS Sync"
        title={meta.title}
        subtitle={meta.subtitle}
        actions={<GithubLinkButton href={meta.githubUrl} label="View folder" />}
      />

      <section>
        <SectionHeader
          icon={Icon}
          label={`Managed ${meta.title}`}
          meta={`${items.length} ${items.length === 1 ? 'entry' : 'entries'}`}
        />
        {items.length === 0 ? (
          <div className="border-t border-dashed border-border/60 py-6 text-center text-sm opacity-60">
            No entries yet.
          </div>
        ) : cat === 'skills' ? (
          <div className="border-t border-border">
            <OriginGroup
              label="L3 Internal"
              items={items.filter((i) => i.origin === 'l3')}
            />
            <OriginGroup
              label="Community"
              items={items.filter((i) => i.origin === 'community')}
            />
          </div>
        ) : (
          <div className="border-t border-border">
            {items.map((item) => (
              <DistributionRow key={item.id} item={item} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
